import { useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useBetValidation } from '@/hooks/useBetValidation';
import { usePendingReveal } from '@/hooks/usePendingReveal';
import { useLoginModalStore } from '@/stores/login-modal-store';

export type GameType = 'coinflip' | 'limbo' | 'dice' | 'plinko' | 'range' | 'lootbox' | 'suiroyale' | (string & {});

type PendingRevealApi = ReturnType<typeof usePendingReveal>;

export type GamePlayHelpers = {
	game: GameType;
	balanceBetId: string | null;
	finalize: PendingRevealApi['finalize'];
	setPayout: PendingRevealApi['setPayout'];
	setGas?: PendingRevealApi['setGas'];
	registerBetId: PendingRevealApi['registerBetId'];
};

export type GamePlayAbortReason = 'notLoggedIn' | 'validationFailed' | 'betInvalid';

type RunGamePlayOptions<Response> = {
	betAmount: number;
	toBaseUnits: (value: number) => number;
	currencyType?: string;
	validators?: Array<() => boolean>;
	skipDefaultValidation?: boolean;
	createTransaction: (stakeBaseUnits: number) => Promise<any> | any;
	onBeforeExecution?: () => void;
	onAbort?: (reason: GamePlayAbortReason) => void;
	onBalanceReserved?: (helpers: GamePlayHelpers) => void;
	onSuccess: (response: Response, helpers: GamePlayHelpers) => void | Promise<void>;
	onError?: (error: unknown, helpers: GamePlayHelpers) => void;
	onFinally?: (helpers: GamePlayHelpers) => void;
};

type ControllerOptions = {
	setTransactionPending?: (pending: boolean) => void;
	onRequireLogin?: () => void;
};

export function useGameTransactionController(game: GameType, controllerOptions: ControllerOptions = {}) {
	const { isLogged, signTransaction, executeTransaction } = useAuth();
	const validateBet = useBetValidation();
	const pendingReveal = usePendingReveal();
	const { openLoginModal } = useLoginModalStore();
	const { setTransactionPending, onRequireLogin } = controllerOptions;

	const runGamePlay = useCallback(
		async <Response>(options: RunGamePlayOptions<Response>) => {
			const {
				betAmount,
				toBaseUnits,
				currencyType,
				validators = [],
				skipDefaultValidation = false,
				createTransaction,
				onBeforeExecution,
				onAbort,
				onBalanceReserved,
				onSuccess,
				onError,
				onFinally,
			} = options;

			if (!isLogged) {
				(onRequireLogin ?? openLoginModal)();
				onAbort?.('notLoggedIn');
				return;
			}

			for (const validator of validators) {
				if (validator() === false) {
					onAbort?.('validationFailed');
					return;
				}
			}

			if (!skipDefaultValidation && !validateBet(betAmount)) {
				onAbort?.('betInvalid');
				return;
			}

			setTransactionPending?.(true);
			onBeforeExecution?.();

			let stakeBaseUnits = 0;
			let balanceBetId: string | null = null;
			let registeredBetId: string | null = null;

			const registerBetId = (args: Parameters<PendingRevealApi['registerBetId']>[0]) => {
				registeredBetId = args.betId;
				pendingReveal.registerBetId(args);
			};
			const helpersBase: Omit<GamePlayHelpers, 'balanceBetId'> = {
				game,
				finalize: pendingReveal.finalize,
				setPayout: pendingReveal.setPayout,
				setGas: pendingReveal.setGas,
				registerBetId,
			};

			try {
				stakeBaseUnits = toBaseUnits(betAmount);
				const tx = await createTransaction(stakeBaseUnits);
				const { bytes, signature, reportTransactionEffects } = await signTransaction(tx);
				({ balanceBetId } = pendingReveal.start({ game, stakeBaseUnits, currencyType }));
				onBalanceReserved?.({ ...helpersBase, balanceBetId });
				const response = await executeTransaction.mutateAsync({ bytes, signature, reportTransactionEffects });
				await onSuccess(response as Response, { ...helpersBase, balanceBetId });
			} catch (error) {
				if (balanceBetId) {
					// Refund stake into the pending balance state so UI balance snaps back on failure.
					if (Number.isFinite(stakeBaseUnits) && stakeBaseUnits > 0) {
						helpersBase.setPayout({ balanceBetId, payoutBaseUnits: stakeBaseUnits });
						helpersBase.setGas?.({ balanceBetId, gasBaseUnits: 0 });
					}
					helpersBase.finalize({ balanceBetId, betId: registeredBetId ?? undefined });
				}
				onError?.(error, { ...helpersBase, balanceBetId });
			} finally {
				setTransactionPending?.(false);
				onFinally?.({ ...helpersBase, balanceBetId });
			}
		},
		[executeTransaction, game, isLogged, onRequireLogin, openLoginModal, pendingReveal, setTransactionPending, signTransaction, validateBet],
	);

	return { runGamePlay };
}

export type GameTransactionRunner = ReturnType<typeof useGameTransactionController>['runGamePlay'];
